import { useEffect, useMemo, useState } from 'react';
import UiIcon from '../../components/common/UiIcon';
import { createAdminCoupons, disableAdminCoupon, getAdminCoupons } from '../../lib/api';
import type { AdminCouponItem, CouponIssueMode, CouponStatus, SpreadType } from '../../lib/types';
import { AdminBadge, AdminEmptyState, AdminPanel, AdminStatCard, formatDateTime, formatSpreadLabel } from './AdminUi';

const EMPTY_COUNTS: Record<CouponStatus, number> = {
  active: 0,
  claimed: 0,
  used: 0,
  disabled: 0,
};

function toneForCouponStatus(status: CouponStatus): 'success' | 'warning' | 'danger' | 'muted' {
  switch (status) {
    case 'active':
      return 'success';
    case 'claimed':
      return 'warning';
    case 'used':
      return 'muted';
    case 'disabled':
      return 'danger';
    default:
      return 'muted';
  }
}

function statusLabel(status: CouponStatus): string {
  switch (status) {
    case 'active':
      return '사용 가능';
    case 'claimed':
      return '등록 완료';
    case 'used':
      return '사용 완료';
    case 'disabled':
      return '비활성화';
    default:
      return '알 수 없음';
  }
}

function issueModeLabel(issueMode: CouponIssueMode): string {
  return issueMode === 'manual' ? '수동' : '랜덤';
}

export default function AdminCouponsPage() {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<CouponStatus | ''>('');
  const [spreadFilter, setSpreadFilter] = useState<SpreadType | ''>('');
  const [coupons, setCoupons] = useState<AdminCouponItem[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<CouponStatus, number>>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [spread, setSpread] = useState<SpreadType>('three-card');
  const [issueMode, setIssueMode] = useState<CouponIssueMode>('random');
  const [count, setCount] = useState(3);
  const [manualCode, setManualCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisablingId, setIsDisablingId] = useState<string | null>(null);
  const [createdCoupons, setCreatedCoupons] = useState<AdminCouponItem[]>([]);

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await getAdminCoupons({
          query: query || undefined,
          status: statusFilter,
          spread: spreadFilter,
          limit: 150,
        });

        if (!disposed) {
          setCoupons(response.coupons);
          setStatusCounts(response.statusCounts);
          setErrorMessage(null);
        }
      } catch (error) {
        if (!disposed) {
          const apiError = error as Error;
          setErrorMessage(apiError.message || '쿠폰 목록을 불러오지 못했습니다.');
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      disposed = true;
    };
  }, [query, spreadFilter, statusFilter]);

  const totalCount = useMemo(
    () => statusCounts.active + statusCounts.claimed + statusCounts.used + statusCounts.disabled,
    [statusCounts]
  );

  const reloadCoupons = async () => {
    const response = await getAdminCoupons({
      query: query || undefined,
      status: statusFilter,
      spread: spreadFilter,
      limit: 150,
    });
    setCoupons(response.coupons);
    setStatusCounts(response.statusCounts);
  };

  const handleCreateCoupons = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response = await createAdminCoupons({
        spread,
        issueMode,
        count: issueMode === 'random' ? count : undefined,
        code: issueMode === 'manual' ? manualCode : undefined,
      });

      setCreatedCoupons(response.coupons);
      setSuccessMessage(
        issueMode === 'manual'
          ? '수동 쿠폰 1개를 발급했습니다.'
          : `랜덤 쿠폰 ${response.coupons.length}개를 발급했습니다.`
      );
      if (issueMode === 'manual') {
        setManualCode('');
      }
      await reloadCoupons();
    } catch (error) {
      const apiError = error as Error;
      setErrorMessage(apiError.message || '쿠폰 발급에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisableCoupon = async (coupon: AdminCouponItem) => {
    if (coupon.status === 'used' || coupon.status === 'disabled') {
      return;
    }

    setIsDisablingId(coupon.id);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      await disableAdminCoupon(coupon.id);
      setSuccessMessage(`${coupon.code} 쿠폰을 비활성화했습니다.`);
      await reloadCoupons();
    } catch (error) {
      const apiError = error as Error;
      setErrorMessage(apiError.message || '쿠폰 비활성화에 실패했습니다.');
    } finally {
      setIsDisablingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <AdminStatCard label="전체 쿠폰" value={totalCount} />
        <AdminStatCard label="사용 가능" value={statusCounts.active} />
        <AdminStatCard label="등록 완료" value={statusCounts.claimed} />
        <AdminStatCard label="사용 완료" value={statusCounts.used} />
        <AdminStatCard label="비활성화" value={statusCounts.disabled} />
      </div>

      <AdminPanel
        title="쿠폰 발급"
        subtitle="랜덤 코드와 수동 코드를 모두 지원합니다. 스프레드별 1회 무료 리딩 쿠폰을 여기서 생성합니다."
      >
        <form className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]" onSubmit={handleCreateCoupons}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm text-[#d8bfa1]/80">스프레드</span>
              <select
                value={spread}
                onChange={(event) => setSpread(event.target.value as SpreadType)}
                className="w-full border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
              >
                <option value="one-card">1카드</option>
                <option value="three-card">3카드</option>
                <option value="celtic-cross">10카드</option>
              </select>
            </label>

            <label className="space-y-2">
              <span className="text-sm text-[#d8bfa1]/80">발급 방식</span>
              <select
                value={issueMode}
                onChange={(event) => setIssueMode(event.target.value as CouponIssueMode)}
                className="w-full border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
              >
                <option value="random">랜덤 코드</option>
                <option value="manual">수동 코드</option>
              </select>
            </label>

            {issueMode === 'random' ? (
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm text-[#d8bfa1]/80">발급 수량</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={count}
                  onChange={(event) => setCount(Math.max(1, Math.min(50, Number(event.target.value) || 1)))}
                  className="w-full border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
                />
              </label>
            ) : (
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm text-[#d8bfa1]/80">수동 코드</span>
                <input
                  value={manualCode}
                  onChange={(event) => setManualCode(event.target.value.toUpperCase())}
                  placeholder="예: MYOAN-THREE-GIFT-001"
                  className="w-full border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
                />
              </label>
            )}
          </div>

          <div className="flex flex-col justify-between gap-4 border border-[#8a6a4b]/25 bg-black/15 p-4">
            <div className="space-y-2 text-sm leading-6 text-[#d8bfa1]/78">
              <p>발급 즉시 상태는 `사용 가능`입니다.</p>
              <p>사용자가 등록하면 `등록 완료`, 실제 리딩에 쓰이면 `사용 완료`로 바뀝니다.</p>
            </div>
            <button
              type="submit"
              disabled={isSubmitting || (issueMode === 'manual' && !manualCode.trim())}
              className="inline-flex items-center justify-center gap-2 border border-[#8a6a4b]/45 bg-[#241913] px-4 py-3 text-sm font-medium text-[#f3dfc5] hover:bg-[#2d2019] disabled:opacity-60"
            >
              <UiIcon name="sparkles" className="h-4 w-4" />
              {isSubmitting ? '발급 중...' : '쿠폰 발급'}
            </button>
          </div>
        </form>

        {successMessage && (
          <div className="mt-4 border border-emerald-500/35 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
            {successMessage}
          </div>
        )}

        {createdCoupons.length > 0 && (
          <div className="mt-4 border border-[#8a6a4b]/25 bg-[#120d0b] p-4">
            <p className="text-sm font-medium text-[#f4e2cb]">방금 발급한 쿠폰</p>
            <div className="mt-3 grid gap-2">
              {createdCoupons.map((coupon) => (
                <div key={coupon.id} className="flex flex-wrap items-center justify-between gap-3 border border-[#8a6a4b]/20 bg-black/15 px-3 py-2">
                  <div>
                    <p className="font-mono text-sm text-[#f7d48a]">{coupon.code}</p>
                    <p className="mt-1 text-xs text-[#d8bfa1]/70">{formatSpreadLabel(coupon.spread)} / {issueModeLabel(coupon.issueMode)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void navigator.clipboard?.writeText(coupon.code)}
                    className="border border-[#8a6a4b]/35 bg-[#241913] px-3 py-2 text-xs text-[#f3dfc5] hover:bg-[#2d2019]"
                  >
                    복사
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </AdminPanel>

      <AdminPanel title="쿠폰 조회" subtitle="상태, 스프레드, 코드 검색으로 발급 현황과 사용 이력을 확인합니다.">
        <div className="grid gap-3 md:grid-cols-[1fr_180px_180px]">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="쿠폰 코드 검색"
            className="w-full border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as CouponStatus | '')}
            className="w-full border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          >
            <option value="">전체 상태</option>
            <option value="active">사용 가능</option>
            <option value="claimed">등록 완료</option>
            <option value="used">사용 완료</option>
            <option value="disabled">비활성화</option>
          </select>
          <select
            value={spreadFilter}
            onChange={(event) => setSpreadFilter(event.target.value as SpreadType | '')}
            className="w-full border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          >
            <option value="">전체 스프레드</option>
            <option value="one-card">1카드</option>
            <option value="three-card">3카드</option>
            <option value="celtic-cross">10카드</option>
          </select>
        </div>

        {errorMessage && (
          <div className="mt-4 border border-rose-500/35 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {errorMessage}
          </div>
        )}

        <div className="mt-4 space-y-3">
          {loading ? (
            <p className="text-sm text-[#d8bfa1]/75">쿠폰 목록을 불러오고 있습니다...</p>
          ) : coupons.length === 0 ? (
            <AdminEmptyState title="조회된 쿠폰이 없습니다" body="필터 조건을 바꾸거나 새 쿠폰을 발급해 주세요." />
          ) : (
            coupons.map((coupon) => (
              <article key={coupon.id} className="border border-[#8a6a4b]/30 bg-[#140f0d]/90 p-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <AdminBadge label={statusLabel(coupon.status)} tone={toneForCouponStatus(coupon.status)} />
                      <AdminBadge label={formatSpreadLabel(coupon.spread)} tone="muted" />
                      <AdminBadge label={issueModeLabel(coupon.issueMode)} tone="accent" />
                    </div>
                    <div>
                      <p className="font-mono text-base font-semibold text-[#f7d48a]">{coupon.code}</p>
                      <p className="mt-1 text-sm text-[#d8bfa1]/72">
                        발급 {formatDateTime(coupon.createdAt)}
                        {coupon.createdByEmail ? ` / ${coupon.createdByEmail}` : ''}
                      </p>
                    </div>
                    <div className="grid gap-2 text-sm text-[#d8bfa1]/76 md:grid-cols-3">
                      <p>
                        등록: {coupon.claimedAt ? `${formatDateTime(coupon.claimedAt)}${coupon.claimedByEmail ? ` / ${coupon.claimedByEmail}` : ''}` : '-'}
                      </p>
                      <p>
                        사용: {coupon.usedAt ? `${formatDateTime(coupon.usedAt)}${coupon.usedByEmail ? ` / ${coupon.usedByEmail}` : ''}` : '-'}
                      </p>
                      <p>
                        비활성화: {coupon.disabledAt ? `${formatDateTime(coupon.disabledAt)}${coupon.disabledReason ? ` / ${coupon.disabledReason}` : ''}` : '-'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => void navigator.clipboard?.writeText(coupon.code)}
                      className="border border-[#8a6a4b]/35 bg-[#241913] px-3 py-2 text-sm text-[#f3dfc5] hover:bg-[#2d2019]"
                    >
                      복사
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDisableCoupon(coupon)}
                      disabled={coupon.status === 'used' || coupon.status === 'disabled' || isDisablingId === coupon.id}
                      className="border border-[#8a2f2f]/45 bg-[#311715] px-3 py-2 text-sm text-[#ffd8d8] hover:bg-[#3d1d1b] disabled:opacity-60"
                    >
                      {isDisablingId === coupon.id ? '처리 중...' : '비활성화'}
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </AdminPanel>
    </div>
  );
}
